import React, { createContext, useContext, useState, useEffect } from 'react';
import api from './api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  // On mount, try /auth/me — works via httpOnly cookie OR legacy localStorage token
  useEffect(() => {
    // Self-heal: if the localStorage says we're impersonating but the /auth/me
    // response comes back with a role that matches the stored user, the flag
    // is orphaned — clear it. This unblocks admins whose previous impersonation
    // session was never cleanly ended.
    const cleanupOrphanedImpersonation = (meData) => {
      if (localStorage.getItem('impersonating') !== 'true') return;
      const storedUserStr = localStorage.getItem('user');
      try {
        const storedUser = storedUserStr ? JSON.parse(storedUserStr) : null;
        // If /auth/me says we're admin OR there is no admin_backup_token to
        // return to, the impersonation state is stale — clear it.
        if (meData?.role === 'admin' || !localStorage.getItem('admin_backup_token')) {
          localStorage.removeItem('impersonating');
          localStorage.removeItem('admin_backup_token');
          localStorage.removeItem('admin_backup_user');
          if (storedUser && storedUser.id !== meData?.id) {
            // Also refresh the stored user snapshot to match reality
            localStorage.setItem('user', JSON.stringify(meData));
          }
        }
      } catch { /* ignore */ }
    };

    api.get('/auth/me')
      .then(response => {
        setUser(response.data);
        cleanupOrphanedImpersonation(response.data);
        if (response.data.requires_password_change) {
          setRequiresPasswordChange(true);
        }
      })
      .catch(() => {
        // Not authenticated — clear any stale legacy token AND impersonation flags
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('impersonating');
        localStorage.removeItem('admin_backup_token');
        localStorage.removeItem('admin_backup_user');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = async (email, password, role) => {
    const response = await api.post('/auth/login', { email, password, role: role || '' });
    // Backend now sets httpOnly cookie on successful login.
    // We still keep the token in localStorage for legacy components that build
    // their own axios instances; over time these are being migrated to the
    // shared `api` client which uses cookies via withCredentials.
    // Clear any stale impersonation state so admin actions aren't blocked
    // by a residual non-admin Bearer token from a previous session.
    localStorage.removeItem('impersonating');
    localStorage.removeItem('admin_backup_token');
    localStorage.removeItem('admin_backup_user');
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }
    const userData = response.data.user;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    if (response.data.requires_password_change) {
      setRequiresPasswordChange(true);
    }
    try {
      const meRes = await api.get('/auth/me');
      setUser(meRes.data);
    } catch {}
    return userData;
  };

  const register = async (email, password, name, role, parentEmail) => {
    const payload = { email, password, name, role };
    if (role === 'student' && parentEmail) {
      payload.parent_email = parentEmail;
    }
    const response = await api.post('/auth/register', payload);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }
    setUser(response.data.user);
    return response.data.user;
  };

  const clearPasswordChangeFlag = () => {
    setRequiresPasswordChange(false);
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Also clear impersonation state so a fresh admin session isn't polluted.
    localStorage.removeItem('impersonating');
    localStorage.removeItem('admin_backup_token');
    localStorage.removeItem('admin_backup_user');
    setUser(null);
    setRequiresPasswordChange(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, requiresPasswordChange, clearPasswordChangeFlag }}>
      {children}
    </AuthContext.Provider>
  );
};
